import React from "react";
import Card from "components/card";
import ReactApexChart from "react-apexcharts";

const LiveGauges = ({ configs, latestData }) => {
  if (!configs || configs.length === 0 || !latestData) return null;

  const renderGauge = (cfg, rawValue) => {
    // 1. Neglect values outside the range (clamp them for the visual calculation)
    let visualVal = rawValue;
    if (visualVal < cfg.min) visualVal = cfg.min;
    if (visualVal > cfg.max) visualVal = cfg.max;

    // Calculate percentage for ApexCharts (0 to 100)
    const percent = ((visualVal - cfg.min) / (cfg.max - cfg.min)) * 100;

    // --- CIRCULAR GAUGE (Full Ring) ---
    if (cfg.guage_type === "circular") {
      const options = {
        chart: { type: "radialBar", sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            hollow: { size: "65%" },
            track: { background: "#E2E8F0" },
            dataLabels: {
              name: { show: false },
              value: { offsetY: 8, fontSize: "22px", fontWeight: "700", color: "#2B3674", formatter: () => `${rawValue} ${cfg.units}` }
            }
          }
        },
        fill: { type: "solid", colors: ["#3B82F6"] }, // Blue
        stroke: { lineCap: "round" }
      };
      return <ReactApexChart options={options} series={[percent]} type="radialBar" height={150} />;
    }

    // --- ARC GAUGE (Semi-Circle) ---
    if (cfg.guage_type === "arc") {
      const options = {
        chart: { type: "radialBar", sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            track: { background: "#E2E8F0" },
            dataLabels: {
              name: { show: false },
              value: { offsetY: -5, fontSize: "22px", fontWeight: "700", color: "#2B3674", formatter: () => `${rawValue} ${cfg.units}` }
            }
          }
        },
        fill: { type: "solid", colors: ["#10B981"] }, // Emerald Green
        stroke: { lineCap: "round" }
      };
      return <ReactApexChart options={options} series={[percent]} type="radialBar" height={150} />;
    }

    // --- COMPASS GAUGE (Custom Minimalist SVG) ---
    if (cfg.guage_type === "compass") {
      return (
        <div className="flex flex-col items-center justify-center h-[150px]">
          <div className="relative flex items-center justify-center w-24 h-24 rounded-full border-[3px] border-gray-200 dark:border-navy-700 shadow-sm bg-white dark:bg-navy-800">
            {/* Cardinal Directions */}
            <span className="absolute top-1 text-[10px] font-bold text-gray-400">N</span>
            <span className="absolute bottom-1 text-[10px] font-bold text-gray-400">S</span>
            <span className="absolute right-1 text-[10px] font-bold text-gray-400">E</span>
            <span className="absolute left-1 text-[10px] font-bold text-gray-400">W</span>
            
            {/* Smoothly Rotating Needle */}
            <div style={{ transform: `rotate(${rawValue}deg)`, transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <svg width="12" height="46" viewBox="0 0 12 46">
                <polygon points="6,0 12,23 0,23" fill="#EF4444" /> {/* Red North Pointer */}
                <polygon points="6,46 12,23 0,23" fill="#9CA3AF" /> {/* Gray South Pointer */}
              </svg>
            </div>
          </div>
          <div className="mt-2 text-lg font-bold text-navy-700 dark:text-white">
            {rawValue}{cfg.units}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 w-full">
      {configs.map((cfg, idx) => {
        // Find the matching key in the latest websocket packet
        let rawVal = parseFloat(latestData[cfg.value]);
        if (isNaN(rawVal)) rawVal = 0;

        return (
          <Card key={idx} extra="flex flex-col items-center justify-center p-4">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {cfg.displayName}
            </h4>
            <div className="w-full flex justify-center">
              {renderGauge(cfg, rawVal)}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default LiveGauges;